import { pushWithRegion } from 'actions/NavigationActions'
import * as Analytics from 'analytics/analytics'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'
import Caption from 'components/Luxkit/Typography/Caption'
import { deleteRecentSearch } from 'components/Search/utils'
import { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/noop'
import { encodeSearchParams } from 'lib/searchUtils'
import React, { useCallback } from 'react'
import RecentSearchSuggestionItem from './RecentSearchSuggestionItem'

interface Props {
  recentSearches: Array<App.FullRecentSearch>;
  onDelete?: (item: App.FullRecentSearch) => void;
  closeDropdown?: () => void;
}

function RecentSearchSuggestions(props: Props) {
  const { recentSearches, onDelete = noop, closeDropdown = noop } = props
  const dispatch = useAppDispatch()

  const memberId = useAppSelector(state => state.auth.account.memberId)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  const onDeleteClick = useCallback((recentSearch: App.FullRecentSearch) => {
    deleteRecentSearch(recentSearch.recentSearchId, memberId!)
    onDelete(recentSearch)
  }, [memberId, onDelete])

  const onRecentSearchClick = useCallback((recentSearch: App.FullRecentSearch) => {
    const { dates, rooms } = recentSearch
    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }

    const newSearch = encodeSearchParams({
      flexibleMonths: dates?.flexibleMonths,
      flexibleNights: dates?.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE,
      dates: {
        checkIn: dates.checkIn,
        checkOut: dates.checkOut,
      },
      urlSearch: '',
      rooms,
      searchItem: recentSearch.searchItem,
      userSelectedFlexibleMonths: !!dates?.flexibleMonths,
    }).toString()
    const path = pathname.includes('/map') ? '/search/map' : '/search'

    closeDropdown()
    dispatch(pushWithRegion(path, newSearch))
  }, [dispatch, closeDropdown, pathname, regionCode])

  return (
    <div data-testid="search-recent-searches">
      <Caption variant="large" weight="bold">Recent searches</Caption>
      {recentSearches.map(recentSearch => <RecentSearchSuggestionItem
        key={recentSearch.recentSearchId}
        recentSearch={recentSearch}
        onClick={onRecentSearchClick}
        onDelete={onDeleteClick}
      />)}
    </div>
  )
}

export default RecentSearchSuggestions
